import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { warningOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { TRIGGER_RELEASE_CLAUSE_PAYMENT } from '../../../../graphql/mutations/releaseClause';
import { GET_FANTASY_LEAGUE_MEMBERS } from '../../../../graphql/queries/fantasyLeagueMembers';
import { PUBLIC_FANTASY_LEAGUE_MEMBER_INFO } from '../../../../graphql/queries/fantasyLeagueMemberInfo';
import { GET_FANTASY_LEAGUE_MEMBER_PLAYERS } from '../../../../graphql/queries/fantasyLeagueMemberPlayers';
import { GET_TEAM_TAB_DATA } from '../../../../graphql/queries/teamTabData';
import { GET_FANTASY_LEAGUE_TABS_SHELL } from '../../../../graphql/queries/fantasyLeagueTabsShell';
import { GET_MY_FANTASY_LEAGUES } from '../../../../graphql/queries/myFantasyLeagues';
import type {
  TriggerReleaseClausePaymentMutation,
  TriggerReleaseClausePaymentMutationVariables,
  Player,
} from '../../../../types/graphql-generated';
import { PlayerCard } from '../../components/PlayerCard';

interface PayReleaseClauseModalProps {
  isOpen: boolean;
  player: Player;
  releaseClause: number;
  budget: number;
  leagueId: string;
  fantasyLeagueMemberId: string;
  buyerMemberId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const PayReleaseClauseModal = ({
  isOpen,
  player,
  releaseClause,
  budget,
  leagueId,
  fantasyLeagueMemberId,
  buyerMemberId,
  onClose,
  onSuccess,
}: PayReleaseClauseModalProps) => {
  const [errors, setErrors] = useState<string[]>([]);

  const [triggerPayment, { loading }] = useMutation<
    TriggerReleaseClausePaymentMutation,
    TriggerReleaseClausePaymentMutationVariables
  >(TRIGGER_RELEASE_CLAUSE_PAYMENT, {
    update: (cache, { data }) => {
      const result = data?.triggerReleaseClausePayment;
      if (!result || result.errors.length > 0) return;

      // Apollo only refetches active queries. The team tab is normally unmounted
      // while this modal is open, so invalidate both roster cache paths as well.
      // This forces a network request when either roster consumer opens later.
      const buyerCacheId = cache.identify({
        __typename: 'FantasyLeagueMember',
        id: buyerMemberId,
      });
      if (buyerCacheId) {
        cache.evict({ id: buyerCacheId, fieldName: 'fantasyTeamPlayers' });
      }
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'fantasyLeagueMemberPlayers',
        args: { fantasyLeagueMemberId: buyerMemberId },
      });
    },
    refetchQueries: [
      { query: PUBLIC_FANTASY_LEAGUE_MEMBER_INFO, variables: { fantasyLeagueMemberId } },
      { query: GET_FANTASY_LEAGUE_MEMBERS, variables: { id: leagueId } },
      {
        query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS,
        variables: { fantasyLeagueMemberId: buyerMemberId },
      },
      { query: GET_TEAM_TAB_DATA, variables: { fantasyLeagueId: leagueId } },
      { query: GET_FANTASY_LEAGUE_TABS_SHELL, variables: { fantasyLeagueId: leagueId } },
      { query: GET_MY_FANTASY_LEAGUES },
    ],
  });

  const handleConfirm = async () => {
    setErrors([]);
    try {
      const result = await triggerPayment({
        variables: {
          input: { playerId: player.id, fantasyLeagueId: leagueId },
        },
      });

      const mutationErrors = result.data?.triggerReleaseClausePayment?.errors;
      if (mutationErrors && mutationErrors.length > 0) {
        setErrors(mutationErrors);
      } else {
        onSuccess();
      }
    } catch {
      setErrors(['Error al procesar el pago de la cláusula']);
    }
  };

  const handleClose = () => {
    setErrors([]);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Pagar cláusula</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="mb-6">
          <PlayerCard player={player} />
        </div>

        <div className="mb-6 space-y-3 rounded-xl bg-slate-50 px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Importe de la cláusula</span>
            <span className="text-lg font-bold text-slate-900">
              {releaseClause.toLocaleString('es-ES')}€
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Tu presupuesto</span>
            <span className={`font-semibold ${budget < releaseClause ? 'text-red-600' : 'text-slate-800'}`}>
              {budget.toLocaleString('es-ES')}€
            </span>
          </div>
        </div>

        <div className="mb-6 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <IonIcon icon={warningOutline} className="mt-0.5 shrink-0 text-xl text-amber-600" />
          <p className="text-sm leading-relaxed text-amber-800">
            Esta operación es <strong>inmediata e irreversible</strong>. El jugador pasará a tu
            equipo al instante y el propietario actual <strong>no puede rechazarla</strong>.
          </p>
        </div>

        {errors.length > 0 && (
          <div className="mb-4">
            {errors.map((error, index) => (
              <IonText color="danger" key={index}>
                <p className="text-sm">{error}</p>
              </IonText>
            ))}
          </div>
        )}

        <IonButton
          expand="block"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || budget < releaseClause}
          data-testid="confirm-pay-clause"
        >
          {loading ? <IonSpinner name="crescent" /> : `Confirmar pago — ${releaseClause.toLocaleString('es-ES')}€`}
        </IonButton>
      </IonContent>
    </IonModal>
  );
};
